import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { Store } from '@ngrx/store';
import { map, take } from 'rxjs';
import { AppRoutes } from './core/constants/routes.constants';
import { Login } from './core/views/login/login';
import { MobileCheckoutRedirect } from './features/booking/views/checkout-redirect/checkout-redirect';
import { authGuard } from './core/guards/auth.guard';
import { adminGuard, doormanGuard } from './core/guards/role.guard';
import AppState from './app.state';
import { selectSelectedUserKind } from './features/auth/state-management/auth.selectors';
import { SciGuaduaIsAllowedGuard } from '@scifamek/guadua';

export const routes: Routes = [
  {
    path: AppRoutes.ROOT,
    pathMatch: 'full',
    canActivate: [authGuard],
    component: Login, // placeholder, resolve redirects before rendering
    resolve: {
      redirect: () => {
        const store = inject(Store<AppState>);
        const router = inject(Router);
        return store.select(selectSelectedUserKind).pipe(
          take(1),
          map((userKind) => {
            if (userKind === 'doorman') {
              router.navigate([`/${AppRoutes.DOORMAN_DASHBOARD}`]);
            } else {
              router.navigate([`/${AppRoutes.BUILDINGS_LIST}`]);
            }
            return true;
          }),
        );
      },
    },
  },
  { path: AppRoutes.LOGIN, component: Login },
  { path: 'mobile-checkout-redirect', component: MobileCheckoutRedirect },
  {
    path: AppRoutes.DEMO,
    loadComponent: () =>
      import('./core/views/demo-components/demo-components').then((m) => m.DemoComponents),
  },
  {
    path: AppRoutes.BUILDINGS_LIST,
    loadComponent: () =>
      import('./features/building/views/list-buildings/list-buildings').then(
        (m) => m.ListBuildings,
      ),
    canActivate: [authGuard, adminGuard],
  },
  {
    path: AppRoutes.SUPER_ADMIN_DASHBOARD,
    loadComponent: () =>
      import('./features/super-admin/views/super-admin-dashboard/super-admin-dashboard').then(
        (m) => m.SuperAdminDashboard,
      ),
    canActivate: [],
  },
  {
    path: `${AppRoutes.RESIDENT_DETAILED_PROFILE}/:residentId`,
    loadComponent: () =>
      import('./features/resident/views/resident-detailed-profile/resident-detailed-profile').then(
        (m) => m.ResidentDetailedProfile,
      ),
    canActivate: [],
  },
  {
    path: AppRoutes.PACKAGES,
    loadComponent: () =>
      import('./features/package/views/list-packages/list-packages').then((m) => m.ListPackages),
    canActivate: [authGuard, adminGuard],
  },
  {
    path: AppRoutes.BOOKINGS,
    loadComponent: () =>
      import('./features/booking/views/list-bookings/list-bookings').then((m) => m.ListBookings),
    canActivate: [authGuard],
  },
  {
    path: AppRoutes.RESIDENTS,
    loadComponent: () =>
      import('./features/resident/views/list-residents/list-residents').then(
        (m) => m.ListResidents,
      ),
    canActivate: [authGuard],
  },
  {
    path: AppRoutes.BUILDINGS,
    loadComponent: () =>
      import('./features/building/views/list-buildings-overview/list-buildings-overview').then(
        (m) => m.ListBuildingsOverview,
      ),
    canActivate: [authGuard, adminGuard],
  },
  {
    path: AppRoutes.AGENCY_LIST,
    loadComponent: () =>
      import('./features/agency/views/list-agencies/list-agencies').then((m) => m.ListAgencys),
    canActivate: [
      SciGuaduaIsAllowedGuard,
      // authGuard,
    ],
    data: {
      identifier: 'AgencyListView',
    },
  },
  {
    path: AppRoutes.AGENCY_DETAIL,
    loadComponent: () =>
      import('./features/agency/views/agency-detail/agency-detail').then((m) => m.AgencyDetail),
    canActivate: [
      //SciGuaduaIsAllowedGuard,
      // authGuard,
    ],
    // data: {
    //   identifier: 'AgencyDetailView',
    // },
  },
  {
    path: AppRoutes.BUILDING_ADMIN_DASHBOARD,
    loadComponent: () =>
      import('./features/building-admin/views/building-admin-dashboard/building-admin-dashboard').then(
        (x) => x.BuildingAdminDashboard,
      ),
    canActivate: [
      //SciGuaduaIsAllowedGuard,
      // authGuard,
    ],
    // data: {
    //   identifier: 'AgencyDetailView',
    // },
  },
  {
    path: AppRoutes.BUILDINGS_CREATE,
    loadComponent: () =>
      import('./features/building/views/create-building/create-building').then(
        (m) => m.CreateBuilding,
      ),
    canActivate: [authGuard, adminGuard],
  },
  {
    path: AppRoutes.AUTH_MANAGEMENT,
    loadComponent: () =>
      import('./features/auth/views/auth-management/auth-management').then((m) => m.AuthManagement),
    canActivate: [authGuard, adminGuard],
  },
  {
    path: AppRoutes.AUTH_MANAGEMENT_BY_BUILDING,
    loadComponent: () =>
      import('./features/auth/views/auth-management-by-admin/auth-management-by-admin').then(
        (m) => m.AuthManagementByBuilding,
      ),
    canActivate: [authGuard],
  },
  {
    path: AppRoutes.CATEGORIES,
    loadComponent: () =>
      import('./features/category/views/list-categories/list-categories').then(
        (m) => m.ListCategories,
      ),
    canActivate: [authGuard],
  },
  {
    path: AppRoutes.VENDORS,
    loadComponent: () =>
      import('./features/vendor/views/list-vendors/list-vendors').then((m) => m.ListVendors),
    canActivate: [authGuard, adminGuard],
  },
  {
    path: AppRoutes.VENDORS_CREATE,
    loadComponent: () =>
      import('./features/vendor/views/create-vendor/create-vendor').then((m) => m.CreateVendor),
    canActivate: [authGuard, adminGuard],
  },
  {
    path: AppRoutes.MEMBERSHIPS,
    loadComponent: () =>
      import('./features/membership/views/list-memberships/list-memberships').then(
        (m) => m.ListMemberships,
      ),
    canActivate: [authGuard, adminGuard],
  },
  {
    path: AppRoutes.DOORMAN_DASHBOARD,
    loadComponent: () =>
      import('./features/doorman/views/doorman-dashboard/doorman-dashboard').then(
        (m) => m.DoormanDashboard,
      ),
    canActivate: [authGuard, doormanGuard],
  },
  {
    path: AppRoutes.DOORMAN_PROFILE,
    loadComponent: () =>
      import('./features/doorman/views/doorman-profile/doorman-profile').then(
        (m) => m.DoormanProfile,
      ),
    canActivate: [authGuard, doormanGuard],
  },
  {
    path: AppRoutes.VENDOR_PAYMENTS,
    loadComponent: () =>
      import('./features/vendor-payment/views/list-vendor-payments/list-vendor-payments').then(
        (m) => m.ListVendorPayments,
      ),
    canActivate: [authGuard, adminGuard],
  },
  {
    path: AppRoutes.VENDOR_PAYMENT_SUMMARY,
    loadComponent: () =>
      import('./features/vendor-payment/views/vendor-payment-summary/vendor-payment-summary').then(
        (m) => m.VendorPaymentSummary,
      ),
    canActivate: [authGuard, adminGuard],
  },
];
